import React, { useState } from 'react';
import { loadUsers } from '../../../actions/companyActions';
import { Link, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import { hasPermission } from '../../auth/authUtils';
import permissionTypes from '../../../constants/permissionTypes';
import Gravatar from 'react-gravatar';
import { Sparklines, SparklinesCurve } from 'react-sparklines';
import YesNoLabel from '../../common/YesNoLabel';
import { useTranslation } from 'react-i18next';

export interface CompanyUsersListProps {
  id: string | undefined;
  currentUser: any;
  reload?: any;
}

function CompanyUsersList({ id, currentUser, reload }: CompanyUsersListProps) {
  const location = useLocation();
  const allColumns = [
    'fullName',
    'requestActivity',
    'keyActivity',
    'emailAddress',
    'isAuthorised',
    'workflowsCanRequest',
    'workflowsCanApprove',
    'openRequests',
    'completeRequests',
    'requestsOverSla',
  ];
  const defaultVisibleColumns = allColumns.filter(
    (x) => x !== 'keyActivity' && x !== 'creditLimit'
  );
  const [users, setUsers] = useState<any>();
  const { t } = useTranslation();
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filterChange,
    isLoading,
  } = useTableSettings(
    'companyuserlist-2.14-settings',
    getTableSettingsDefaults({
      defaultSort: 'fullName',
      defaultVisibleColumns,
    }),
    { id },
    (id) => loadUsers(id),
    setUsers,
    reload,
    t('Failed to load company users')
  );

  let columnsConfig = [
    {
      Key: 'fullName',
      Label: t('Name'),
      width: '30%',
      Render: (item: any) => (
        <div style={{ display: 'flex' }}>
          <Gravatar
            email={item?.username}
            size={20}
            rating="pg"
            default="retro"
            className="img-circle me-3"
          />
          {hasPermission(currentUser, permissionTypes.MANAGE_USERS) ? (
            <Link to={'/user/' + item.id}>{item.fullName}</Link>
          ) : (
            item.fullName
          )}
        </div>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'requestActivity',
      Label: t('Request activity'),
      width: '110px',
      Resize: false,
      Render: (item: any) => (
        <Sparklines
          data={[
            item.requestActivityScore30to60Days,
            item.requestActivityScore7to30Days,
            item.requestActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.requestActivityScore30to60Days +
                item.requestActivityScore7to30Days +
                item.requestActivityScoreLast7Days >
                0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#46b29d" />
        </Sparklines>
      ),
    },
    {
      Key: 'keyActivity',
      Label: t('Key activity'),
      width: '110px',
      Resize: false,
      Render: (item: any) => (
        <Sparklines
          data={[
            item.keyActivityScore30to60Days,
            item.keyActivityScore7to30Days,
            item.keyActivityScoreLast7Days,
          ]}
          min={0}
          max={100}
          height={20}
          width={60}
          style={{
            opacity:
              item.keyActivityScore30to60Days +
                item.keyActivityScore7to30Days +
                item.keyActivityScoreLast7Days >
                0
                ? 1
                : 0.5,
          }}
        >
          <SparklinesCurve color="#bb5ebf" />
        </Sparklines>
      ),
    },
    {
      Key: 'emailAddress',
      Label: t('Email'),
      width: '30%',
      Render: (item: any) => item.emailAddress,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'isAuthorised',
      Label: t('Authorised'),
      width: '10%',
      Render: (item: any) => <YesNoLabel value={item.isAuthorised} />,
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, el.target.value)}
            >
              <option value="">{t('--- any ---')}</option>
              <option value="true">{t('Yes - available to external')}</option>
              <option value="false">{t('No - not available to external')}</option>
            </select>
          );
        },
      },
    },
    {
      Key: 'openRequests',
      Label: t('Open requests'),
      width: '10%',
      className: 'text-right num no-wrap',
      Render: (item: any) => CommaNumber_NoDecimal(item.openRequests, undefined),
    },
    {
      Key: 'workflowsCanRequest',
      Label: t('Workflows can request'),
      width: '10%',
      Render: (item: any) => item.workflowsCanRequest ?? 'All allowed',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={params.get(`f_${[col.Key]}`) || ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'workflowsCanApprove',
      Label: t('Workflows can approve'),
      width: '10%',
      Render: (item: any) => item.workflowsCanApprove ?? 'All allowed',
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={params.get(`f_${[col.Key]}`) || ''}
            onChange={(value: string) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'completeRequests',
      Label: t('Complete requests'),
      width: '10%',
      className: 'text-right num no-wrap',
      Render: (item: any) => CommaNumber_NoDecimal(item.completeRequests, undefined),
    },
    {
      Key: 'requestsOverSla',
      Label: t('Requests over SLA'),
      width: '10%',
      className: 'text-right num no-wrap',
      Render: (item: any) => CommaNumber_NoDecimal(item.requestsOverSla, undefined),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return users ? (
    <KwPanel className="mb-4">
      <header className="mb-2">
        <h2 className="no-margin">{t('Users')}</h2>
      </header>
      <Table
        columns={columns}
        allColumns={allColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        rows={users.data.map((x: any) => {
          return { Key: x.id, ...x };
        })}
        totalRowCount={users.totalRowCount}
        filteredRowCount={users.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filterChange={filterChange}
      />
    </KwPanel>
  ) : (
    <Loading></Loading>
  );
}
export default CompanyUsersList;
